import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePassiveApiActivity } from './usePassiveApiActivity';
import { mediaUrlService } from '../services/mediaUrlService';
import {
  getGcsV4SignedMediaExpiryMs,
  pickDurableMediaUri,
  pickMediaUri,
} from '../utils/mediaSources';

const DIRECT_SIGNED_URL_REFRESH_SKEW_MS = 30 * 1000;
const MAX_REMEMBERED_BROWSER_FAILURES = 8;

interface ResolvedMediaUrlOptions {
  durableSource?: string | null;
  videoId?: string | null;
  segmentId?: string | null;
  /** Stable physical asset identity, including preview kind. */
  cacheKey?: string | null;
}

interface ResolvedMediaUrlState {
  resolvedUrl: string | null;
  isLoading: boolean;
  resolutionFailed: boolean;
  isReady?: boolean;
  markReady?: (loadedUrl?: string | null) => void;
  /**
   * Recover a browser media failure from the durable authority. Repeating the
   * callback for the same failed grant is terminal, preventing deterministic
   * token issuance from creating an error/remint loop.
   */
  refreshAfterError: (failedUrl?: string | null) => boolean;
}

interface InternalResolvedState {
  sourceKey: string;
  resolvedUrl: string | null;
  isLoading: boolean;
  resolutionFailed: boolean;
}

interface RecoveryState {
  sourceKey: string;
  generation: number;
  useDurableAuthority: boolean;
  failedUrl: string | null;
}

interface BrowserRecoveryGuard {
  sourceKey: string;
  activeFailedUrl: string | null;
  attemptedFailedUrls: string[];
}

const decodeImageBeforeSwap = async (url: string): Promise<boolean> => {
  if (typeof Image === 'undefined') {
    return true;
  }
  const image = new Image();
  image.decoding = 'async';
  if (typeof image.decode === 'function') {
    image.src = url;
    if (image.complete && image.naturalWidth > 0) {
      return true;
    }
    try {
      await image.decode();
      return true;
    } catch {
      return false;
    }
  }
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (decoded: boolean) => {
      if (settled) return;
      settled = true;
      image.onload = null;
      image.onerror = null;
      resolve(decoded);
    };
    // Install handlers before assigning src. Cached resources can dispatch a
    // synchronous load/error in older WebViews that do not implement decode().
    image.onload = () => finish(true);
    image.onerror = () => finish(false);
    image.src = url;
    if (image.complete) {
      finish(image.naturalWidth > 0);
    }
  });
};

const peekReadyPreviewUrl = (
  cacheKey?: string | null,
  authority?: string | null,
): string | null => {
  if (typeof (mediaUrlService as any).peekReadyPreviewUrl !== 'function') {
    return null;
  }
  return mediaUrlService.peekReadyPreviewUrl(cacheKey, authority);
};

export const useResolvedMediaUrl = (
  source?: string | null,
  options: ResolvedMediaUrlOptions = {},
): ResolvedMediaUrlState => {
  const normalizedSource = useMemo(() => pickMediaUri(source), [source]);
  const normalizedDurableSource = useMemo(
    () => pickDurableMediaUri(options.durableSource, normalizedSource),
    [normalizedSource, options.durableSource],
  );
  const sourceKey = useMemo(
    () => JSON.stringify([
      normalizedSource,
      normalizedDurableSource,
      options.videoId || null,
      options.segmentId || null,
      options.cacheKey || null,
    ]),
    [normalizedDurableSource, normalizedSource, options.cacheKey, options.segmentId, options.videoId],
  );
  const [recovery, setRecovery] = useState<RecoveryState>({
    sourceKey: '',
    generation: 0,
    useDurableAuthority: false,
    failedUrl: null,
  });
  const useDurableAuthority = (
    recovery.sourceKey === sourceKey
    && recovery.useDurableAuthority
  );
  const directSourceExpiryMs = normalizedSource
    ? getGcsV4SignedMediaExpiryMs(normalizedSource)
    : null;
  const directSourceNeedsRefresh = Boolean(
    directSourceExpiryMs !== null
    && Date.now() >= directSourceExpiryMs - DIRECT_SIGNED_URL_REFRESH_SKEW_MS
    && normalizedDurableSource
    && normalizedDurableSource !== normalizedSource
  );
  const resolutionSource = useDurableAuthority || directSourceNeedsRefresh
    ? normalizedDurableSource || normalizedSource
    : normalizedSource;
  const previewCacheContext = useMemo(() => ({
    cacheKey: options.cacheKey,
    durableSource: normalizedDurableSource,
  }), [normalizedDurableSource, options.cacheKey]);
  const mediaResolutionContext = useMemo(() => (
    options.cacheKey
      ? {
          videoId: options.videoId,
          segmentId: options.segmentId,
          ...previewCacheContext,
        }
      : {
          videoId: options.videoId,
          segmentId: options.segmentId,
        }
  ), [options.cacheKey, options.segmentId, options.videoId, previewCacheContext]);
  const initialSnapshot = useMemo(() => {
    const readyUrl = peekReadyPreviewUrl(
      options.cacheKey,
      normalizedDurableSource,
    );
    const candidateUrl = mediaUrlService.peekResolvedUrl(
      resolutionSource,
      previewCacheContext,
    );
    return {
      readyUrl,
      resolvedUrl: readyUrl || candidateUrl,
    };
  }, [
    normalizedDurableSource,
    options.cacheKey,
    previewCacheContext,
    resolutionSource,
  ]);
  const [state, setState] = useState<InternalResolvedState>({
    sourceKey,
    resolvedUrl: initialSnapshot.resolvedUrl,
    isLoading: Boolean(
      resolutionSource
      && !initialSnapshot.resolvedUrl
      && mediaUrlService.needsResolution(resolutionSource)
    ),
    resolutionFailed: false,
  });
  const canObserve = usePassiveApiActivity();
  const [, setReadyGeneration] = useState(0);
  const automaticRecoveryRef = useRef<BrowserRecoveryGuard>({
    sourceKey,
    activeFailedUrl: null,
    attemptedFailedUrls: [],
  });
  const lastDecodedUrlRef = useRef<{ sourceKey: string; url: string } | null>(
    initialSnapshot.readyUrl ? { sourceKey, url: initialSnapshot.readyUrl } : null,
  );
  const replacementRetryRef = useRef<{ sourceKey: string; attempts: number }>({
    sourceKey,
    attempts: 0,
  });

  useEffect(() => {
    automaticRecoveryRef.current = {
      sourceKey,
      activeFailedUrl: null,
      attemptedFailedUrls: [],
    };
    const cachedReadyUrl = peekReadyPreviewUrl(
      options.cacheKey,
      normalizedDurableSource,
    );
    lastDecodedUrlRef.current = cachedReadyUrl
      ? { sourceKey, url: cachedReadyUrl }
      : null;
    replacementRetryRef.current = { sourceKey, attempts: 0 };
  }, [normalizedDurableSource, options.cacheKey, sourceKey]);

  useEffect(() => {
    if (!canObserve) return;
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;

    const finishBrowserRecovery = (failedUrl?: string | null) => {
      const guard = automaticRecoveryRef.current;
      if (
        guard.sourceKey === sourceKey
        && (!failedUrl || guard.activeFailedUrl === failedUrl)
      ) {
        automaticRecoveryRef.current = {
          ...guard,
          activeFailedUrl: null,
        };
      }
    };

    const getRetainedDecodedUrl = (): string | null => {
      const cachedReadyUrl = peekReadyPreviewUrl(
        options.cacheKey,
        normalizedDurableSource,
      );
      if (cachedReadyUrl) {
        lastDecodedUrlRef.current = { sourceKey, url: cachedReadyUrl };
        return cachedReadyUrl;
      }
      return lastDecodedUrlRef.current?.sourceKey === sourceKey
        ? lastDecodedUrlRef.current.url
        : null;
    };

    const scheduleRefresh = () => {
      const delayMs = mediaUrlService.getRefreshDelayMs(
        resolutionSource,
        Date.now(),
        previewCacheContext,
      );
      if (delayMs === null || !Number.isFinite(delayMs)) {
        return;
      }
      refreshTimer = setTimeout(() => {
        if (cancelled) {
          return;
        }
        setRecovery((current) => ({
          sourceKey,
          generation: (
            current.sourceKey === sourceKey
              ? current.generation + 1
              : 1
          ),
          useDurableAuthority: Boolean(normalizedDurableSource),
          failedUrl: null,
        }));
      }, Math.max(1, delayMs));
    };

    const scheduleReplacementRetry = (failedUrl?: string | null) => {
      const currentRetry = replacementRetryRef.current.sourceKey === sourceKey
        ? replacementRetryRef.current
        : { sourceKey, attempts: 0 };
      if (currentRetry.attempts >= 2) {
        return;
      }
      replacementRetryRef.current = {
        sourceKey,
        attempts: currentRetry.attempts + 1,
      };
      refreshTimer = setTimeout(() => {
        if (cancelled) return;
        setRecovery((current) => ({
          sourceKey,
          generation: current.sourceKey === sourceKey
            ? current.generation + 1
            : 1,
          useDurableAuthority: Boolean(normalizedDurableSource),
          failedUrl: failedUrl || null,
        }));
      }, 1000 * (currentRetry.attempts + 1));
    };

    const publishCandidate = async (candidateUrl: string): Promise<boolean> => {
      const retainedDecodedUrl = getRetainedDecodedUrl();
      if (!retainedDecodedUrl || retainedDecodedUrl === candidateUrl) {
        if (retainedDecodedUrl === candidateUrl) {
          lastDecodedUrlRef.current = { sourceKey, url: candidateUrl };
        }
        setState({
          sourceKey,
          resolvedUrl: candidateUrl,
          isLoading: false,
          resolutionFailed: false,
        });
        replacementRetryRef.current = { sourceKey, attempts: 0 };
        return true;
      }

      // Keep the already-decoded frame on screen while the replacement is
      // fetched and decoded off-DOM. The visible <img> swaps only after the
      // browser confirms the new resource is renderable.
      setState({
        sourceKey,
        resolvedUrl: retainedDecodedUrl,
        isLoading: true,
        resolutionFailed: false,
      });
      const decoded = await decodeImageBeforeSwap(candidateUrl);
      if (cancelled) return false;
      if (!decoded) {
        mediaUrlService.markPreviewFailed(options.cacheKey, candidateUrl);
        setState({
          sourceKey,
          resolvedUrl: retainedDecodedUrl,
          isLoading: false,
          resolutionFailed: false,
        });
        scheduleReplacementRetry(candidateUrl);
        return false;
      }

      mediaUrlService.markPreviewReady(options.cacheKey, candidateUrl);
      lastDecodedUrlRef.current = { sourceKey, url: candidateUrl };
      replacementRetryRef.current = { sourceKey, attempts: 0 };
      setState({
        sourceKey,
        resolvedUrl: candidateUrl,
        isLoading: false,
        resolutionFailed: false,
      });
      return true;
    };

    const cleanup = () => {
      cancelled = true;
      if (refreshTimer !== null) {
        clearTimeout(refreshTimer);
      }
    };

    const isErrorRecovery = (
      recovery.sourceKey === sourceKey
      && recovery.failedUrl !== null
    );
    const cachedUrl = isErrorRecovery
      ? null
      : mediaUrlService.peekResolvedUrl(resolutionSource, previewCacheContext);
    if (cachedUrl) {
      void publishCandidate(cachedUrl).then((published) => {
        if (published && !cancelled) scheduleRefresh();
      });
      return cleanup;
    }

    if (!resolutionSource) {
      setState({
        sourceKey,
        resolvedUrl: null,
        isLoading: false,
        resolutionFailed: false,
      });
      if (isErrorRecovery) finishBrowserRecovery(recovery.failedUrl);
      return cleanup;
    }

    if (!mediaUrlService.needsResolution(resolutionSource)) {
      void publishCandidate(resolutionSource).then((published) => {
        if (cancelled) return;
        if (isErrorRecovery) finishBrowserRecovery(recovery.failedUrl);
        if (published) scheduleRefresh();
      });
      return cleanup;
    }

    const retainedDecodedUrl = getRetainedDecodedUrl();
    setState({
      sourceKey,
      resolvedUrl: retainedDecodedUrl,
      isLoading: true,
      resolutionFailed: false,
    });
    const resolution = isErrorRecovery
      ? mediaUrlService.refreshFailedUrl(
          resolutionSource,
          recovery.failedUrl,
          mediaResolutionContext,
        )
      : mediaUrlService.resolveUrl(resolutionSource, mediaResolutionContext);
    void resolution
      .then((resolvedUrl) => {
        if (cancelled) {
          return;
        }
        if (resolvedUrl === null) {
          const retainedUrl = getRetainedDecodedUrl();
          setState({
            sourceKey,
            resolvedUrl: retainedUrl,
            isLoading: false,
            resolutionFailed: retainedUrl === null,
          });
          if (retainedUrl) scheduleReplacementRetry(recovery.failedUrl);
          if (isErrorRecovery) finishBrowserRecovery(recovery.failedUrl);
          return;
        }
        void publishCandidate(resolvedUrl).then((published) => {
          if (cancelled) return;
          if (isErrorRecovery) finishBrowserRecovery(recovery.failedUrl);
          if (published) scheduleRefresh();
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        const retainedUrl = getRetainedDecodedUrl();
        setState({
          sourceKey,
          resolvedUrl: retainedUrl,
          isLoading: false,
          resolutionFailed: retainedUrl === null,
        });
        if (retainedUrl) scheduleReplacementRetry(recovery.failedUrl);
        if (isErrorRecovery) finishBrowserRecovery(recovery.failedUrl);
      });

    return cleanup;
  }, [
    canObserve,
    mediaResolutionContext,
    normalizedDurableSource,
    options.cacheKey,
    options.segmentId,
    options.videoId,
    previewCacheContext,
    recovery.failedUrl,
    recovery.generation,
    recovery.sourceKey,
    resolutionSource,
    sourceKey,
  ]);

  const visibleState = state.sourceKey === sourceKey
    ? state
    : {
        sourceKey,
        resolvedUrl: peekReadyPreviewUrl(
          options.cacheKey,
          normalizedDurableSource,
        ) || mediaUrlService.peekResolvedUrl(resolutionSource, previewCacheContext),
        isLoading: Boolean(
          resolutionSource
          && mediaUrlService.needsResolution(resolutionSource)
        ),
        resolutionFailed: false,
      };
  const refreshAfterError = useCallback((failedUrl?: string | null): boolean => {
    const observedUrl = String(
      failedUrl || visibleState.resolvedUrl || '',
    ).trim();
    if (
      options.cacheKey
      && typeof (mediaUrlService as any).markPreviewFailed === 'function'
    ) {
      mediaUrlService.markPreviewFailed(options.cacheKey, observedUrl);
    }
    const currentGuard = automaticRecoveryRef.current.sourceKey === sourceKey
      ? automaticRecoveryRef.current
      : {
          sourceKey,
          activeFailedUrl: null,
          attemptedFailedUrls: [],
        };
    if (currentGuard.activeFailedUrl) {
      // Multiple <img> error events for one shared grant are common while the
      // forced replacement is in flight. The first event owns recovery; later
      // events keep the decoded fallback visible and must not blank the tile.
      return true;
    }
    if (observedUrl && currentGuard.attemptedFailedUrls.includes(observedUrl)) {
      setState({
        sourceKey,
        resolvedUrl: null,
        isLoading: false,
        resolutionFailed: true,
      });
      return false;
    }

    const canRecoverFromDurableAuthority = Boolean(
      normalizedDurableSource
      && observedUrl
      && (
        normalizedDurableSource !== observedUrl
        || mediaUrlService.needsResolution(normalizedDurableSource)
      )
    );
    if (!canRecoverFromDurableAuthority) {
      setState({
        sourceKey,
        resolvedUrl: null,
        isLoading: false,
        resolutionFailed: true,
      });
      return false;
    }

    automaticRecoveryRef.current = {
      sourceKey,
      activeFailedUrl: observedUrl,
      attemptedFailedUrls: [
        ...currentGuard.attemptedFailedUrls,
        observedUrl,
      ].slice(-MAX_REMEMBERED_BROWSER_FAILURES),
    };
    const retainedDecodedUrl = peekReadyPreviewUrl(
      options.cacheKey,
      normalizedDurableSource,
    ) || (
      lastDecodedUrlRef.current?.sourceKey === sourceKey
        ? lastDecodedUrlRef.current.url
        : null
    );
    setState({
      sourceKey,
      resolvedUrl: retainedDecodedUrl,
      isLoading: true,
      resolutionFailed: false,
    });
    setRecovery((current) => ({
      sourceKey,
      generation: (
        current.sourceKey === sourceKey
          ? current.generation + 1
          : 1
      ),
      useDurableAuthority: true,
      failedUrl: observedUrl,
    }));
    return true;
  }, [
    normalizedDurableSource,
    options.cacheKey,
    sourceKey,
    visibleState.resolvedUrl,
  ]);

  const markReady = useCallback((loadedUrl?: string | null): void => {
    if (!options.cacheKey) return;
    const observedUrl = pickMediaUri(loadedUrl, visibleState.resolvedUrl);
    if (!observedUrl) return;
    if (typeof (mediaUrlService as any).markPreviewReady !== 'function') return;
    mediaUrlService.markPreviewReady(options.cacheKey, observedUrl);
    lastDecodedUrlRef.current = { sourceKey, url: observedUrl };
    setReadyGeneration((generation) => generation + 1);
  }, [options.cacheKey, sourceKey, visibleState.resolvedUrl]);

  const isReady = Boolean(
    options.cacheKey
    && visibleState.resolvedUrl
    && typeof (mediaUrlService as any).isPreviewReady === 'function'
    && mediaUrlService.isPreviewReady(options.cacheKey, visibleState.resolvedUrl)
  );

  return {
    resolvedUrl: visibleState.resolvedUrl,
    isLoading: visibleState.isLoading,
    resolutionFailed: visibleState.resolutionFailed,
    isReady,
    markReady,
    refreshAfterError,
  };
};
